// The carving, as one picture of the whole shell per pumpkin: every direction from its centre, round the pumpkin
// across and top to bottom down (equirectangular). R cut through, G how deep it is scraped, B the stencil's trace.
// It is built from the stencil and then the carves in order, each applied once and kept: a piece of skin the cuts
// leave with no hold on the rest falls out when the knife lifts, and stays out whatever is drawn after it. A carve
// still being drawn is laid over the top for the screen, and never changes what is kept.

import type { Carve, Stencil } from '../types';
import { SHEET_PHI as PHI, SHEET_THETA as THETA } from '../pumpkin/shape';
import { isCut, makeCanvas, pathsForStencil } from './raster';
import type { Pt } from './geom';

const TAU = Math.PI * 2;
/** how far the sheet's x and y reach: round the back to the seam, and over the top */
export const SPAN_X = Math.PI / PHI, SPAN_Y = Math.PI / 2 / THETA;
/** pieces are found on a grid of this size whatever the picture's, so the same piece falls out on every device */
const GW = 1024, GH = 512;

export interface Box { x0: number; x1: number; y0: number; y1: number }   // pixels, half-open; x unwrapped, it may run past either edge
export interface Upload { x: number; y: number; w: number; h: number; data: Uint8Array }

let W = 2048, H = 1024;
const cache = new Map<string, CarveRaster>();
const CACHED = 3;

/** the picture's width (twice its height); the renderer sets it from the tier. Pieces don't depend on it */
export function setCarveSize(w: number) {
  if (w === W) return;
  W = w; H = w / 2;
  cache.clear();
  tables = null; cov = null; dcov = null;
}
export const carveSize = (): [number, number] => [W, H];

export class CarveRaster {
  readonly W = W;
  readonly H = H;
  readonly px = new Uint8Array(W * H * 4);
  /** each carve applied so far, as it was */
  ops: string[] = [];
  /** the stencil's ink the picture was started from */
  ink = '';
  /** the whole stencil cut through at once (Carve it for me) */
  cut = false;
  /** changed since it was last handed to the screen */
  dirty: Box | null = null;
  /** how open the shell is overall, cut and scraped, as a share of the sheet's area: what one candle's light is spread by */
  open: [number, number] = [0, 0];
  /** the top is cut away, and the stalk with it */
  stalk = false;
  grid = new Uint8Array(GW * GH);
  draft: { sig: string; n: number; box: Box | null; op: Paint; az: number } | null = null;
  /** the holes' edges, the draft's included, per tile of the picture (`edgeTiles`): see `traceEdges` */
  edges: (Float32Array | null)[] = new Array((W / TILE) * Math.ceil(H / TILE)).fill(null);
  /** counts each change to `edges` */
  edgeGen = 0;
  // opaque black: alpha is each pixel's top byte, little-endian (every device this runs on)
  constructor() { new Uint32Array(this.px.buffer).fill(0xff000000); }
}

const TILE = 64;

/** the carving of pumpkin `id` for this stencil, brought up to date; a carve with id 'c-draft' is the one being drawn */
export function carveRaster(id: string, stencil: Stencil): CarveRaster {
  let r = cache.get(id);
  if (r) { cache.delete(id); cache.set(id, r); }
  const all = (stencil.carve ?? []).filter(c => c.points.length);
  const draft = all.length && all[all.length - 1].id === 'c-draft' ? all[all.length - 1] : null;
  const ops = draft ? all.slice(0, -1) : all;
  const sigs = ops.map(c => JSON.stringify(c));
  const ink = JSON.stringify([stencil.parts, stencil.strokes, !!stencil.mirror, isCut(stencil)]);
  const prefix = r && r.ink === ink && r.ops.length <= sigs.length && r.ops.every((s, i) => s === sigs[i]);
  const rebuilt = !r || !prefix;
  if (!r || !prefix) {
    const old = r?.draft?.box;
    if (old) eachPixel(old, p => { draftBuf()[p] = 0; });
    r = new CarveRaster();
    cache.set(id, r);
    while (cache.size > CACHED) cache.delete(cache.keys().next().value!);
    r.ink = ink;
    r.cut = isCut(stencil);
    // a blank stencil is all skin: no need to look at every pixel to find that out (three of them on the first load)
    if (stamp(r, stencil)) regrid(r, full()); else r.grid.fill(1);
    if (isCut(stencil)) dropIslands(r, full());
    r.dirty = full();
  }
  const fresh = r.ops.length < sigs.length;
  for (let i = r.ops.length; i < ops.length; i++) { apply(r, ops[i]); r.ops.push(sigs[i]); }
  if (fresh || rebuilt) stats(r);
  syncDraft(r, draft);
  return r;
}

/** what changed since last time, the carve being drawn laid over it, in at most two strips (split at the seam) */
export function takeUploads(r: CarveRaster, all = false): Upload[] {
  const b = all ? full() : r.dirty;
  r.dirty = null;
  if (!b) return [];
  // nothing cut yet (the paper being drawn rebuilds this on every move of the pen): no holes, so no edges to trace
  if (r.cut || r.ops.length || r.draft) traceEdges(r, b);
  const out: Upload[] = [];
  const y0 = Math.max(0, b.y0), y1 = Math.min(r.H, b.y1);
  for (const [x0, x1] of spans(b)) {
    const w = x1 - x0, h = y1 - y0;
    if (w <= 0 || h <= 0) continue;
    const data = new Uint8Array(w * h * 4), d = r.draft, dc = d ? draftBuf() : null;
    for (let y = 0; y < h; y++) {
      const from = ((y0 + y) * r.W + x0) * 4;
      data.set(r.px.subarray(from, from + w * 4), y * w * 4);
      if (!d || !dc) continue;
      for (let x = 0; x < w; x++) { const c = dc[(y0 + y) * r.W + x0 + x]; if (c) paint(data, (y * w + x) * 4, c, d.op); }
    }
    out.push({ x: x0, y: y0, w, h, data });
  }
  return out;
}

/** cut through (R or a full scrape) at a sheet point, or the stencil's ink not yet cut */
export function carvedAt(r: CarveRaster, x: number, y: number, kind: 'cut' | 'ink'): boolean {
  const k = pixelOf(r, x, y) * 4, px = r.px;
  const cut = px[k] >= 128 || px[k + 1] >= 245;
  return kind === 'cut' ? cut : !cut && px[k + 2] >= 64;
}

// ---- where things are

const full = (): Box => ({ x0: 0, x1: W, y0: 0, y1: H });
const colOf = (az: number) => (az / TAU + 0.5) * W;          // continuous: pixel i's centre is i + 0.5
const rowOf = (el: number) => (0.5 - el / Math.PI) * H;
const wrap = (i: number) => ((i % W) + W) % W;
function pixelOf(r: CarveRaster, x: number, y: number) {
  const i = wrap(Math.floor(colOf(x * PHI))), j = Math.min(H - 1, Math.max(0, Math.floor(rowOf(y * THETA))));
  return j * r.W + i;
}

let tables: { sinA: Float32Array; cosA: Float32Array; sinE: Float32Array; cosE: Float32Array; gw: Float32Array } | null = null;
function T() {
  if (tables) return tables;
  const sinA = new Float32Array(W), cosA = new Float32Array(W), sinE = new Float32Array(H), cosE = new Float32Array(H), gw = new Float32Array(GH);
  for (let i = 0; i < W; i++) { const a = ((i + 0.5) / W - 0.5) * TAU; sinA[i] = Math.sin(a); cosA[i] = Math.cos(a); }
  for (let j = 0; j < H; j++) { const e = (0.5 - (j + 0.5) / H) * Math.PI; sinE[j] = Math.sin(e); cosE[j] = Math.cos(e); }
  for (let j = 0; j < GH; j++) gw[j] = Math.cos((0.5 - (j + 0.5) / GH) * Math.PI);
  return (tables = { sinA, cosA, sinE, cosE, gw });
}

function union(a: Box | null, b: Box | null): Box | null {
  if (!a) return b;
  if (!b) return a;
  const y0 = Math.min(a.y0, b.y0), y1 = Math.max(a.y1, b.y1);
  if (a.x1 - a.x0 >= W || b.x1 - b.x0 >= W) return { x0: 0, x1: W, y0, y1 };
  // b moved round by whole turns to sit nearest a
  const shift = Math.round(((a.x0 + a.x1) - (b.x0 + b.x1)) / 2 / W) * W;
  const x0 = Math.min(a.x0, b.x0 + shift), x1 = Math.max(a.x1, b.x1 + shift);
  return x1 - x0 >= W ? { x0: 0, x1: W, y0, y1 } : { x0, x1, y0, y1 };
}

/** a box's columns as runs inside the picture */
function spans(b: Box): [number, number][] {
  if (b.x1 - b.x0 >= W) return [[0, W]];
  const x0 = wrap(b.x0), x1 = x0 + (b.x1 - b.x0);
  return x1 <= W ? [[x0, x1]] : [[x0, W], [0, x1 - W]];
}

function eachPixel(b: Box, f: (p: number) => void) {
  const y0 = Math.max(0, b.y0), y1 = Math.min(H, b.y1);
  for (const [x0, x1] of spans(b)) for (let y = y0; y < y1; y++) for (let x = x0, p = y * W + x0; x < x1; x++, p++) f(p);
}

// ---- the stencil

/** the stencil's ink, drawn on the sheet's own rectangle of the picture with the sheet's own transform, as the old flat mask was */
/** the stencil's ink and cuts drawn into the picture; false when there was nothing to draw */
function stamp(r: CarveRaster, stencil: Stencil): boolean {
  const shapes = pathsForStencil(stencil);
  if (!shapes.length) return false;
  const sx0 = Math.floor(colOf(-PHI)), sx1 = Math.ceil(colOf(PHI)), sy0 = Math.floor(rowOf(THETA)), sy1 = Math.ceil(rowOf(-THETA));
  const w = sx1 - sx0, h = sy1 - sy0;
  let canvas: ReturnType<typeof makeCanvas>;
  try { canvas = makeCanvas(w); } catch { return false; }
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
  if (!ctx || typeof ctx.getImageData !== 'function') return false;
  const kx = (W / TAU) * PHI, ky = (H / Math.PI) * THETA;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);
  ctx.setTransform(kx, 0, 0, -ky, colOf(0) - sx0, rowOf(0) - sy0);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const cut = isCut(stencil);
  for (const s of shapes) {
    // the trace in blue, a scrape at half and a cut full, the darker winning; once cut, R through and G scraped as well
    const colour = s.tool === 'eraser' ? '#000' : !cut ? (s.tool === 'scraper' ? '#000080' : '#00f') : s.tool === 'scraper' ? '#00cc80' : '#f0f';
    ctx.globalCompositeOperation = s.tool === 'eraser' ? 'source-over' : !cut ? 'lighten' : 'lighter';
    ctx.beginPath();
    for (const poly of s.polys) {
      poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      if (!s.line) ctx.closePath();
    }
    if (s.line) { ctx.strokeStyle = colour; ctx.lineWidth = s.width; ctx.stroke(); } else { ctx.fillStyle = colour; ctx.fill('nonzero'); }
  }
  const d = ctx.getImageData(0, 0, w, h).data;
  for (let y = 0; y < h; y++) {
    const ry = sy0 + y;
    if (ry < 0 || ry >= H) continue;
    for (let x = 0; x < w; x++) {
      const s = (y * w + x) * 4, t = (ry * W + wrap(sx0 + x)) * 4;
      r.px[t] = d[s]; r.px[t + 1] = d[s + 1]; r.px[t + 2] = d[s + 2];
    }
  }
  return true;
}

// ---- carves

interface Paint { tool: 'knife' | 'scraper' | 'fill'; g: number; keep: number; clip: boolean }

const knifeLike = (c: Carve) => c.tool === 'knife' || (c.tool as string) === 'poker' || (c.tool === 'scraper' && c.depth >= 0.99);
function paintOf(c: Carve): Paint {
  return {
    tool: knifeLike(c) ? 'knife' : c.tool === 'fill' ? 'fill' : 'scraper',
    // a scrape adds its depth pass by pass; a fill takes the cut back by its depth of what was there
    g: Math.round(Math.max(0.04, Math.min(1, c.depth)) * 160),
    keep: 1 - Math.max(0.05, Math.min(1, c.depth)),
    clip: !!c.clip,
  };
}

/** coverage c (0..255) of a carve on pixel k of an RGBA array */
function paint(a: Uint8Array, k: number, c: number, o: Paint) {
  // an old carve kept within the lines only reaches the stencil's ink
  if (o.clip) c = Math.round(c * Math.min(1, a[k + 2] / 128));
  if (o.tool === 'knife') { if (c > a[k]) a[k] = c; return; }
  if (o.tool === 'scraper') { a[k + 1] = Math.min(255, a[k + 1] + Math.round((o.g * c) / 255)); return; }
  refill(a, k, c / 255, o.keep);
}

/**
 * A fill over pixel k, `cover` of it (0..1): it takes the scrape back by its depth of what was there, and closes any
 * hole it covers, a hole being the whole wall gone: a shallow fill puts back a thin skin, scraped to `keep`
 */
function refill(a: Uint8Array, k: number, cover: number, keep: number) {
  a[k + 1] = Math.round(Math.max(a[k + 1], a[k]) * (1 - cover * (1 - keep)));
  a[k] = Math.round(a[k] * (1 - cover));
}

let cov: Uint8Array | null = null, dcov: Uint8Array | null = null;
/** scratch, left clear after each use; the carve being drawn has its own, kept while it is drawn */
const coverBuf = () => (cov ??= new Uint8Array(W * H));
const draftBuf = () => (dcov ??= new Uint8Array(W * H));

const dirOf = (x: number, y: number): [number, number, number] => {
  const az = x * PHI, el = y * THETA, ce = Math.cos(el);
  return [ce * Math.sin(az), Math.sin(el), ce * Math.cos(az)];
};

/**
 * Round dabs along the stroke, a third of their radius apart, each a cap on the sphere so it stays round over the
 * top and across the seam; the coverage is the most of any dab, so a stroke never adds to itself. Returns the box it
 * covered and whether it came back over itself (it may have shut a piece in).
 */
function strokeInto(buf: Uint8Array, pts: Pt[], width: number, from: number, az0: number): { box: Box | null; self: boolean; az: number } {
  const t = T(), r = Math.max(0.0015, width / 2) * THETA, kp = H / Math.PI, ra = r + 1.5 / kp, cr = Math.cos(ra);
  const dabs: [number, number, number][] = [];
  let box: Box | null = null, self = false, azPrev = az0;
  const dab = (d: [number, number, number]) => {
    const [dx, dy, dz] = d, se0 = dy, ce0 = Math.hypot(dx, dz);
    let az = Math.atan2(dx, dz);
    if (Number.isFinite(azPrev)) az += Math.round((azPrev - az) / TAU) * TAU;   // unwrapped along the stroke
    azPrev = az;
    const el = Math.atan2(dy, ce0);
    const j0 = Math.max(0, Math.floor(rowOf(el + ra))), j1 = Math.min(H - 1, Math.ceil(rowOf(el - ra)));
    let bx0 = Infinity, bx1 = -Infinity, wide = false;
    for (let j = j0; j <= j1; j++) {
      const se = t.sinE[j], ce = t.cosE[j], den = ce * ce0;
      let i0: number, i1: number;
      if (den < 1e-7) {
        if (se * se0 < cr) continue;
        i0 = 0; i1 = W - 1;
      } else {
        const c = (cr - se * se0) / den;
        if (c >= 1) continue;
        if (c <= -1) { i0 = 0; i1 = W - 1; } else {
          const da = Math.acos(c);
          i0 = Math.floor(colOf(az - da) - 0.5); i1 = Math.ceil(colOf(az + da) - 0.5);
          if (i1 - i0 >= W - 1) { i0 = 0; i1 = W - 1; }
        }
      }
      const fullRow = i0 === 0 && i1 === W - 1;
      for (let i = i0; i <= i1; i++) {
        const ii = fullRow ? i : wrap(i), dot = dx * ce * t.sinA[ii] + dy * se + dz * ce * t.cosA[ii];
        const ang = Math.sqrt(Math.max(0, 2 - 2 * dot));
        const v = (r - ang) * kp + 0.5;
        if (v <= 0) continue;
        const c = v >= 1 ? 255 : Math.round(v * 255), p = j * W + ii;
        if (c > buf[p]) buf[p] = c;
      }
      if (fullRow) wide = true; else { bx0 = Math.min(bx0, i0); bx1 = Math.max(bx1, i1 + 1); }
    }
    if (wide || Number.isFinite(bx0)) box = union(box, wide ? { x0: 0, x1: W, y0: j0, y1: j1 + 1 } : { x0: bx0, x1: bx1, y0: j0, y1: j1 + 1 });
    // back over itself: a dab well along the stroke that overlaps an earlier one
    if (!self) for (let m = 0; m < dabs.length - 9; m++) {
      const e = dabs[m], ch = (e[0] - dx) ** 2 + (e[1] - dy) ** 2 + (e[2] - dz) ** 2;
      if (ch < (1.6 * r) ** 2) { self = true; break; }
    }
    dabs.push(d);
  };
  // straight on the sheet between points, not the short way round the sphere: the points were drawn and thinned on the
  // sheet, and a ring right round the lid thins to two points at the same spot, one either side of the seam
  const step = r / 3;
  let prev = pts[Math.max(0, from - 1)];
  if (from === 0) dab(dirOf(prev[0], prev[1]));
  for (let k = Math.max(1, from); k < pts.length; k++) {
    const next = pts[k], dx = next[0] - prev[0], dy = next[1] - prev[1];
    const low = prev[1] * next[1] < 0 ? 0 : Math.min(Math.abs(prev[1]), Math.abs(next[1]));   // nearest the equator, where a step across is longest
    const n = Math.max(1, Math.ceil(Math.hypot(dx * PHI * Math.cos(low * THETA), dy * THETA) / step));
    for (let s = 1; s <= n; s++) dab(dirOf(prev[0] + dx * s / n, prev[1] + dy * s / n));
    prev = next;
  }
  return { box, self, az: azPrev };
}

/** a closed ring, filled solid: old click-cuts and holes put back were stored as their outline */
function polyInto(buf: Uint8Array, pts: Pt[]): Box | null {
  const P = pts.map(([x, y]) => [colOf(x * PHI), rowOf(y * THETA)] as Pt);
  let y0 = Infinity, y1 = -Infinity, x0 = Infinity, x1 = -Infinity;
  for (const [x, y] of P) { y0 = Math.min(y0, y); y1 = Math.max(y1, y); x0 = Math.min(x0, x); x1 = Math.max(x1, x); }
  const j0 = Math.max(0, Math.floor(y0)), j1 = Math.min(H - 1, Math.ceil(y1));
  const xs: number[] = [];
  for (let j = j0; j <= j1; j++) {
    const yc = j + 0.5;
    xs.length = 0;
    for (let k = 0; k < P.length; k++) {
      const a = P[k], b = P[(k + 1) % P.length];
      if ((a[1] <= yc) !== (b[1] <= yc)) xs.push(a[0] + ((yc - a[1]) / (b[1] - a[1])) * (b[0] - a[0]));
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      for (let i = Math.ceil(xs[k] - 0.5); i <= Math.floor(xs[k + 1] - 0.5); i++) buf[j * W + wrap(i)] = 255;
    }
  }
  return Number.isFinite(x0) ? { x0: Math.floor(x0) - 1, x1: Math.ceil(x1) + 1, y0: j0, y1: j1 + 1 } : null;
}

function apply(r: CarveRaster, c: Carve) {
  const o = paintOf(c), buf = coverBuf();
  let box: Box | null, touched = false, self = false;
  if (c.flood) {
    box = flood(r, c);
    self = true;
  } else {
    const pts = (c.points.length === 1 ? [c.points[0], [c.points[0][0] + 0.001, c.points[0][1]]] : c.points) as Pt[];
    const s = strokeInto(buf, c.closed ? [...pts, pts[0]] : pts, c.width, 0, NaN);
    box = s.box; self = s.self;
    if (c.closed && pts.length > 2) { box = union(box, polyInto(buf, pts)); self = true; }
    if (box) eachPixel(box, p => {
      const v = buf[p];
      if (!v) return;
      const k = p * 4;
      touched ||= r.px[k] >= 128 || r.px[k + 1] >= 245;
      paint(r.px, k, v, o);
      buf[p] = 0;
    });
  }
  if (!box) return;
  r.dirty = union(r.dirty, box);
  regrid(r, box);
  if (o.tool === 'knife' && (touched || self)) dropIslands(r, box);
}

// ---- floods: a click cuts out the whole shape under it, or puts a whole hole back

const isCutPx = (a: Uint8Array, k: number) => a[k] >= 128 || a[k + 1] >= 245;

/** the 4 neighbours of pixel p, round the seam and over each pole */
function around(p: number, f: (q: number) => void, w = W, h = H) {
  const x = p % w, y = (p - x) / w;
  f(y * w + (x === 0 ? w - 1 : x - 1));
  f(y * w + (x === w - 1 ? 0 : x + 1));
  f(y === 0 ? (x + w / 2) % w : p - w);
  f(y === h - 1 ? (h - 1) * w + (x + w / 2) % w : p + w);
}

function flood(r: CarveRaster, c: Carve): Box | null {
  const start = pixelOf(r, c.points[0][0], c.points[0][1]), px = r.px, knife = c.tool !== 'fill';
  const on = (p: number) => { const k = p * 4, cut = isCutPx(px, k); return knife ? !cut && px[k + 2] >= 64 : cut; };
  if (!on(start)) return null;
  const mark = coverBuf(), stack = [start];
  let y0 = H, y1 = 0, x0 = Infinity, x1 = -Infinity;
  const hit: number[] = [];
  mark[start] = 1;
  while (stack.length) {
    const p = stack.pop()!;
    hit.push(p);
    around(p, q => { if (!mark[q] && on(q)) { mark[q] = 1; stack.push(q); } });
  }
  // grown a pixel (a fill two) so it takes the soft edge round it too
  const grow = knife ? 1 : 2;
  let edge = hit;
  for (let g = 0; g < grow; g++) {
    const next: number[] = [];
    for (const p of edge) around(p, q => { if (!mark[q]) { mark[q] = 1; next.push(q); } });
    for (const q of next) hit.push(q);
    edge = next;
  }
  const keep = 1 - Math.max(0.05, Math.min(1, c.depth));
  for (const p of hit) {
    mark[p] = 0;
    const k = p * 4, x = p % W, y = (p - x) / W;
    if (knife) px[k] = 255; else refill(px, k, 1, keep);
    y0 = Math.min(y0, y); y1 = Math.max(y1, y + 1); x0 = Math.min(x0, x); x1 = Math.max(x1, x + 1);
  }
  // a region across the seam is taken whole width rather than worked out round it
  return x1 - x0 > W / 2 ? { x0: 0, x1: W, y0, y1 } : { x0, x1, y0, y1 };
}

// ---- pieces that fall out

/** the coarse grid's cells under a box: 1 where the whole cell is skin */
function regrid(r: CarveRaster, b: Box) {
  const k = W / GW, gy0 = Math.max(0, Math.floor(b.y0 / k)), gy1 = Math.min(GH, Math.ceil(b.y1 / k)), px = r.px;
  const gb: Box = { x0: Math.floor(b.x0 / k), x1: Math.ceil(b.x1 / k), y0: gy0, y1: gy1 };
  const cols = gb.x1 - gb.x0 >= GW ? [[0, GW]] : (() => { const a = ((gb.x0 % GW) + GW) % GW, e = a + gb.x1 - gb.x0; return e <= GW ? [[a, e]] : [[a, GW], [0, e - GW]]; })();
  for (const [cx0, cx1] of cols) for (let gy = gy0; gy < gy1; gy++) for (let gx = cx0; gx < cx1; gx++) {
    let skin = 1;
    for (let y = gy * k; y < gy * k + k && skin; y++) for (let x = gx * k; x < gx * k + k; x++) {
      if (isCutPx(px, (y * W + x) * 4)) { skin = 0; break; }
    }
    r.grid[gy * GW + gx] = skin;
  }
}

let labels: Int32Array | null = null;

/**
 * Skin with no hold on the rest falls out: the skin's pieces are found on the coarse grid (round the seam and over
 * the poles), and every piece but the biggest that the carve's box reaches goes through. The cut is then taken out
 * to the piece's own pixels, so no ring of skin is left round where it was.
 */
function dropIslands(r: CarveRaster, b: Box) {
  const t = T(), g = r.grid, lab = (labels ??= new Int32Array(GW * GH));
  lab.fill(0);
  const area: number[] = [0];
  const stack: number[] = [];
  for (let s = 0; s < g.length; s++) {
    if (!g[s] || lab[s]) continue;
    const id = area.length;
    let a = 0;
    lab[s] = id; stack.push(s);
    while (stack.length) {
      const p = stack.pop()!;
      a += t.gw[(p / GW) | 0];
      around(p, q => { if (g[q] && !lab[q]) { lab[q] = id; stack.push(q); } }, GW, GH);
    }
    area.push(a);
  }
  if (area.length <= 2) return;
  let big = 1;
  for (let i = 2; i < area.length; i++) if (area[i] > area[big]) big = i;
  // the pieces the carve reached: in its box grown by two cells
  const k = W / GW, near = new Set<number>();
  const gb: Box = { x0: Math.floor(b.x0 / k) - 2, x1: Math.ceil(b.x1 / k) + 2, y0: Math.max(0, Math.floor(b.y0 / k) - 2), y1: Math.min(GH, Math.ceil(b.y1 / k) + 2) };
  const wide = gb.x1 - gb.x0 >= GW;
  for (let gy = gb.y0; gy < gb.y1; gy++) for (let gx = wide ? 0 : gb.x0; gx < (wide ? GW : gb.x1); gx++) {
    const l = lab[gy * GW + (((gx % GW) + GW) % GW)];
    if (l && l !== big) near.add(l);
  }
  if (!near.size) return;
  // the cells going, and those beside them: the pixel flood is kept to these, so a hair-thin bridge can't carry it off
  const zone = new Uint8Array(GW * GH);
  let y0 = H, y1 = 0;
  for (let s = 0; s < lab.length; s++) {
    if (!near.has(lab[s])) continue;
    zone[s] = 2;
  }
  for (let s = 0; s < lab.length; s++) if (zone[s] === 2) around(s, q => { if (!zone[q]) zone[q] = 1; }, GW, GH);
  const px = r.px, seen = coverBuf(), todo: number[] = [];
  for (let s = 0; s < lab.length; s++) {
    if (zone[s] !== 2) continue;
    const gx = s % GW, gy = (s - gx) / GW;
    for (let y = gy * k; y < gy * k + k; y++) for (let x = gx * k; x < gx * k + k; x++) { const p = y * W + x; seen[p] = 1; todo.push(p); }
    y0 = Math.min(y0, gy * k); y1 = Math.max(y1, gy * k + k);
  }
  const done: number[] = [];
  while (todo.length) {
    const p = todo.pop()!;
    done.push(p);
    px[p * 4] = 255;
    around(p, q => {
      if (seen[q] || isCutPx(px, q * 4)) return;
      const x = q % W, y = (q - x) / W;
      if (!zone[((y / k) | 0) * GW + ((x / k) | 0)]) return;
      seen[q] = 1; todo.push(q);
    });
  }
  for (const p of done) seen[p] = 0;
  const box: Box = { x0: 0, x1: W, y0: Math.max(0, y0 - k), y1: Math.min(H, y1 + k) };
  r.dirty = union(r.dirty, box);
  regrid(r, box);
}

// ---- the carve being drawn

function syncDraft(r: CarveRaster, c: Carve | null) {
  const d = r.draft;
  const sig = c ? JSON.stringify([c.tool, c.width, c.depth, !!c.clip, c.points[0]]) : '';
  const buf = draftBuf();
  if (d && (!c || d.sig !== sig || c.points.length < d.n)) {
    // the last one is gone (kept or dropped): its pixels go back to what is kept
    if (d.box) { eachPixel(d.box, p => { buf[p] = 0; }); r.dirty = union(r.dirty, d.box); }
    r.draft = null;
  }
  if (!c) return;
  const cur = r.draft ?? (r.draft = { sig, n: 0, box: null, op: paintOf(c), az: NaN });
  if (cur.n >= c.points.length) return;
  const pts = (c.points.length === 1 ? [c.points[0], [c.points[0][0] + 0.0005, c.points[0][1]]] : c.points) as Pt[];
  const s = strokeInto(buf, pts, c.width, cur.n, cur.az);
  cur.n = c.points.length; cur.az = s.az;
  cur.box = union(cur.box, s.box);
  r.dirty = union(r.dirty, s.box);
}

// ---- the holes' edges: the renderer stands the cut face of the flesh on them, a strip through the wall

const smooth01 = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const one = new Uint8Array(4);
let field: Float32Array | null = null;

/** how cut pixel p is, 0 to 1 with the edge at a half, as the shaders read it: the draft laid over what is kept */
function cutOf(r: CarveRaster, p: number): number {
  const k = p * 4, px = r.px, d = r.draft;
  let R = px[k], G = px[k + 1];
  const c = d ? draftBuf()[p] : 0;
  if (c) { one[0] = R; one[1] = G; one[2] = px[k + 2]; paint(one, 0, c, d!.op); R = one[0]; G = one[1]; }
  return Math.max(R / 255, smooth01(0.96, 0.99, G / 255));
}

/**
 * Marching squares on the pixel centres, over every tile box b could have changed. A tile keeps its segments as
 * x, y, gx, gy for each end: where it is in the picture's pixels (pixel i's centre at i + 0.5, x possibly one past the
 * seam) and which way is more cut there, the field's gradient carried smoothly along the edge from cell to cell
 */
function traceEdges(r: CarveRaster, b: Box) {
  const TX = W / TILE, TY = Math.ceil(H / TILE), fw = TILE + 3;
  const buf = (field ??= new Float32Array(fw * fw));
  const ty0 = Math.max(0, Math.floor((b.y0 - 2) / TILE)), ty1 = Math.min(TY, Math.ceil((b.y1 + 1) / TILE));
  const txs: number[] = [];
  if (b.x1 - b.x0 >= W - 3) for (let t = 0; t < TX; t++) txs.push(t);
  else for (let t = Math.floor((b.x0 - 2) / TILE), e = Math.ceil((b.x1 + 1) / TILE); t < e && txs.length < TX; t++) txs.push(((t % TX) + TX) % TX);
  const out: number[] = [], g = [0, 0, 0, 0];
  for (let ty = ty0; ty < ty1; ty++) for (const tx of txs) {
    // cells i0..i0+TILE across and j0..j1 down; the field one pixel round them, for the gradient
    const i0 = tx * TILE, j0 = ty * TILE, j1 = Math.min(j0 + TILE, H - 1), fh = j1 - j0 + 3;
    let lo = 1, hi = 0;
    for (let y = 0; y < fh; y++) {
      const j = Math.min(H - 1, Math.max(0, j0 - 1 + y));
      for (let x = 0; x < fw; x++) { const v = cutOf(r, j * W + wrap(i0 - 1 + x)); buf[y * fw + x] = v; if (v < lo) lo = v; if (v > hi) hi = v; }
    }
    const slot = ty * TX + tx;
    if (lo >= 0.5 || hi < 0.5) { r.edges[slot] = null; continue; }
    out.length = 0;
    const F = (x: number, y: number) => buf[y * fw + x];
    const ends = new Float64Array(16), has = [false, false, false, false];
    // where the edge crosses from pixel (ax, ay) to its neighbour (bx, by), field coordinates; four numbers into g at o
    const cross = (ax: number, ay: number, bx: number, by: number, o: number) => {
      const va = F(ax, ay), s = (0.5 - va) / (F(bx, by) - va);
      g[0] = i0 - 1 + ax + (bx - ax) * s + 0.5; g[1] = j0 - 1 + ay + (by - ay) * s + 0.5;
      const gax = (F(ax + 1, ay) - F(ax - 1, ay)) / 2, gay = (F(ax, Math.min(fh - 1, ay + 1)) - F(ax, Math.max(0, ay - 1))) / 2;
      const gbx = (F(bx + 1, by) - F(bx - 1, by)) / 2, gby = (F(bx, Math.min(fh - 1, by + 1)) - F(bx, Math.max(0, by - 1))) / 2;
      g[2] = gax + (gbx - gax) * s; g[3] = gay + (gby - gay) * s;
      if (g[2] * g[2] + g[3] * g[3] < 1e-8) { g[2] = (bx - ax) * Math.sign(F(bx, by) - va); g[3] = (by - ay) * Math.sign(F(bx, by) - va); }
      ends[o] = g[0]; ends[o + 1] = g[1]; ends[o + 2] = g[2]; ends[o + 3] = g[3];
    };
    const seg = (a: number, b: number) => { for (let k = 0; k < 4; k++) out.push(ends[a * 4 + k]); for (let k = 0; k < 4; k++) out.push(ends[b * 4 + k]); };
    for (let y = 1; y <= j1 - j0; y++) for (let x = 1; x <= TILE; x++) {
      const va = F(x, y), vb = F(x + 1, y), vc = F(x + 1, y + 1), vd = F(x, y + 1);
      const ca = va >= 0.5, cb = vb >= 0.5, cc = vc >= 0.5, cd = vd >= 0.5;
      if (ca === cb && cb === cc && cc === cd) continue;
      // the cell's sides: top a-b, right b-c, bottom d-c, left a-d
      if ((has[0] = ca !== cb)) cross(x, y, x + 1, y, 0);
      if ((has[1] = cb !== cc)) cross(x + 1, y, x + 1, y + 1, 4);
      if ((has[2] = cd !== cc)) cross(x, y + 1, x + 1, y + 1, 8);
      if ((has[3] = ca !== cd)) cross(x, y, x, y + 1, 12);
      if (has[0] && has[1] && has[2] && has[3]) {
        // a saddle: the centre decides which corners join
        if ((va + vb + vc + vd) / 4 >= 0.5 === ca) { seg(0, 1); seg(2, 3); } else { seg(0, 3); seg(1, 2); }
      } else {
        const s = has.flatMap((h, k) => (h ? [k] : []));
        if (s.length === 2) seg(s[0], s[1]);
      }
    }
    r.edges[slot] = out.length ? new Float32Array(out) : null;
  }
  r.edgeGen++;
}

// ---- for the light

/** the cut and scraped share of the shell (area-weighted, sampled), over the sheet's area, and whether the top is off */
function stats(r: CarveRaster) {
  const t = T(), px = r.px, step = Math.max(1, W >> 9);
  let cr = 0, cg = 0, top = 0, topN = 0;
  const topRows = Math.ceil(H * 0.035);
  for (let j = 0; j < H; j += step) {
    const w = t.cosE[j];
    for (let i = 0; i < W; i += step) {
      const k = (j * W + i) * 4, a = px[k] / 255, s = px[k + 1] >= 245 ? 1 : 0, c = Math.max(a, s);
      cr += c * w; cg += (px[k + 1] / 255) * (1 - c) * w;
      if (j < topRows) { top += c; topN++; }
    }
  }
  const cells = (W / step) * (H / step), sheet = (2 * PHI * 2 * Math.sin(THETA)) / (4 * Math.PI);
  const scale = (Math.PI / 2) / (cells * sheet);
  r.open = [cr * scale, cg * scale];
  r.stalk = topN > 0 && top / topN > 0.6;
}
